// twoFA.tsx
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Formik, FormikHelpers } from 'formik';
import { Button, Form, Input, Layout } from 'components';
import { StyledInputGroup } from 'components/shared/Input/styles';
import { useAppDispatch, useAppSelector } from 'store/store';
import { selectAuth, selectUser, setTwoFARequired } from 'store/slices/authSlice';
import {Routes, validationSchema2FA} from 'lib';
import { EFromStates } from 'types';
import styled from 'styled-components';
import { get2FATimeout, resend2FA, verify2FA } from 'store/thunks/authThunk';

const StyledContent = styled.div`
  padding: 2rem 1rem 8.8rem;
  max-width: 153rem;
  margin: auto;
  color: ${(props) => (props.theme.id === 'dark' ? props.theme.text1 : props.theme.text2)};

  @media screen and (min-width: 768px) {
    padding: 5rem 1rem 12.1rem;
  }

  .twofa-form-container {
    max-width: 606px;
    margin: auto;
    margin-bottom: 2.4rem;

    @media screen and (min-width: 768px) {
      margin-bottom: 4.2rem;
    }

    .twofa-text {
      text-align: center;
      margin-bottom: 2rem;
    }

    .twofa-buttons {
      display: flex;
      justify-content: center;
      margin-bottom: 2rem;
    }

    .resend-button {
      display: flex;
      justify-content: center;
      margin-bottom: 3rem;
    }
  }
`;

const StyledInputsGroup = styled.div`
  max-width: 39rem;
  margin: auto;
  margin-bottom: 2rem;

  ${StyledInputGroup} {
    &:not(:last-child) {
      margin-bottom: 1.5rem;
    }
  }
`;

const StyledFormHeader = styled.p`
  margin-bottom: 2.5rem;
  text-align: center;
`;

export const TwoFAPage = () => {
    const { user, token, twoFARequired } = useAppSelector(selectAuth);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [formState, setFormState] = useState<EFromStates>(EFromStates.initial);
    const [timeoutSeconds, setTimeoutSeconds] = useState<number>(0);
    const [isResending, setIsResending] = useState<boolean>(false);

    useEffect(() => {
        if (token) {
            dispatch(get2FATimeout({ token })).then((action) => {
                if (get2FATimeout.fulfilled.match(action)) {
                    setTimeoutSeconds(action.payload.timeout);
                }
            });
        }
    }, [dispatch, token]);

    useEffect(() => {
        if (timeoutSeconds > 0) {
            const timer = setInterval(() => {
                setTimeoutSeconds((prev) => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [timeoutSeconds > 0]);

    interface IFormValues {
        code: string;
        formErrors: string;
    }

    const handleSubmit = async (
        values: IFormValues,
        helpers: FormikHelpers<IFormValues>,
    ) => {
        try {
            setFormState(EFromStates.submitting);

            if (!token) {
                throw new Error('Token is missing');
            }

            const resultAction = await dispatch(verify2FA({ token, code: values.code }));

            if (verify2FA.fulfilled.match(resultAction)) {
                dispatch(setTwoFARequired(false));
                navigate(Routes.statisticsPath());
            } else {
                throw new Error(resultAction.error.message || 'Invalid code');
            }

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            setFormState(EFromStates.failure);
            helpers.setErrors({ formErrors: t(`errors.${errorMessage}`) || errorMessage || t('forms.errors.validationError2FACodeInvalid') });
            helpers.setSubmitting(false);
        }
    };

    const handleResend = async () => {
        if (!token || timeoutSeconds > 0 || isResending) return;

        try {
            setIsResending(true);
            const resultAction = await dispatch(resend2FA({ token }));

            if (resend2FA.fulfilled.match(resultAction)) {
                // После успешной переотправки снова запрашиваем таймаут
                const timeoutAction = await dispatch(get2FATimeout({ token }));
                if (get2FATimeout.fulfilled.match(timeoutAction)) {
                    setTimeoutSeconds(timeoutAction.payload.timeout);
                }
            } else {
                console.error('Failed to resend 2FA code');
            }
        } catch (error) {
            console.error('Error during 2FA resend', error);
        } finally {
            setIsResending(false);
        }
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    if (user) return <Navigate to={Routes.statisticsPath()} />;
    if (!token && token != undefined) return <Navigate to={Routes.loginPath()} />;

    return (
        <Layout home>
            <StyledContent>
                <div className="twofa-form-container">
                    <Formik
                        onSubmit={handleSubmit}
                        initialValues={{
                            code: '',
                            formErrors: '',
                        }}
                        validationSchema={validationSchema2FA}
                    >
                        {({
                              handleSubmit,
                              isSubmitting,
                              isValid,
                              submitCount,
                              handleBlur,
                              errors,
                              touched,
                              handleChange,
                              values,
                              setErrors,
                          }) => (
                            <Form
                                onSubmit={handleSubmit}
                                header={
                                    <StyledFormHeader className="p1 semibold">
                                        {t('forms.twofa.header')}
                                    </StyledFormHeader>
                                }
                                formState={formState}
                                setFormState={setFormState}
                                errorMessage={errors.formErrors}
                            >
                                <p className="p2 twofa-text">
                                    {t('forms.twofa.description')}
                                </p>

                                <StyledInputsGroup>
                                    <Input
                                        name="code"
                                        placeholder={t('forms.twofa.codePlaceholder')}
                                        inValid={!!errors.code && touched.code && !!values.code}
                                        disabled={isSubmitting}
                                        error={errors.code}
                                        value={values.code}
                                        onBlur={handleBlur}
                                        onChange={(e) => {
                                            handleChange(e);
                                            if (errors.formErrors) {
                                                setErrors({ ...errors, formErrors: '' });
                                            }
                                        }}
                                    />
                                </StyledInputsGroup>

                                <div className="twofa-buttons">
                                    <Button
                                        type="submit"
                                        title={t('forms.twofa.submitButton')}
                                        disabled={isSubmitting || (!!errors.code && touched.code)}
                                    />
                                </div>

                                <div className="resend-button">
                                    <Button
                                        type="button"
                                        title={timeoutSeconds > 0 ? `${t('forms.twofa.timerText')} ${formatTime(timeoutSeconds)}` : t('forms.twofa.resendButton')}
                                        disabled={isSubmitting || timeoutSeconds > 0 || isResending}
                                        onClick={handleResend}
                                    />
                                </div>

                            </Form>
                        )}
                    </Formik>
                </div>
            </StyledContent>
        </Layout>
    );
};